import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/I18nContext';
import { User, Camera, Mail } from 'lucide-react';
import { toast } from 'sonner';
import type { User as UserType } from '@/services/interfaces/Auth';

interface ProfileSectionProps {
  user: UserType;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({ user }) => {
  const { t } = useTranslation();
  const { updateUserProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user.name || '',
    emailNotifications: true, // 从用户设置中获取
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error(t('profile.nameRequired'));
      return;
    }

    setIsLoading(true);
    try {
      // 调用真实的API更新用户信息
      await updateUserProfile({ name: formData.name });
      setIsEditing(false);
    } catch (error) {
      // 错误处理已经在 AuthContext 中完成
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user.name || '',
      emailNotifications: true,
    });
    setIsEditing(false);
  };

  const handleAvatarClick = () => {
    // TODO: 实现头像上传功能
    toast.info(t('profile.avatarUploadComingSoon'));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          {t('settings.profile')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 头像部分 */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar className="h-20 w-20">
              <AvatarImage src={user.avatar || undefined} />
              <AvatarFallback className="text-lg">
                {user.name?.charAt(0)?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <Button
              size="sm"
              variant="secondary"
              className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full p-0"
              onClick={handleAvatarClick}
            >
              <Camera className="h-4 w-4" />
            </Button>
          </div>
          <div>
            <h3 className="font-medium">{user.name}</h3>
            <p className="text-sm text-muted-foreground">{user.email}</p>
            <p className="text-xs text-muted-foreground">
              {t('profile.memberSince')} {new Date(user.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* 基本信息 */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('profile.displayName')}</Label>
            {isEditing ? (
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('profile.enterName')}
              />
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-sm">{user.name}</span>
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  {t('common.edit')}
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('profile.email')}</Label>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{user.email}</span>
            </div>
          </div>

          {/* 通知设置 */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>{t('profile.emailNotifications')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('profile.emailNotificationsDesc')}
              </p>
            </div>
            <Switch
              checked={formData.emailNotifications}
              onCheckedChange={(checked: boolean) =>
                setFormData({ ...formData, emailNotifications: checked })
              }
            />
          </div>
        </div>

        {/* 操作按钮 */}
        {isEditing && (
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? t('common.saving') : t('common.save')}
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
